// Tiny client-side auth state. Refresh tokens live in the sm_refresh cookie
// (HTTP-only, set by the backend) for web — we deliberately ignore the
// refreshToken in the response body to keep it out of JS-reachable storage.
// The access token is kept in memory only (lost on page reload by design —
// /auth/refresh handles the silent re-issue using the cookie).

import { api } from './api';

let accessToken: string | null = null;
let onChange: (() => void) | null = null;

export type SessionUser = {
  id: string;
  email: string;
  emailVerified: boolean;
  avatarUrl?: string;
  locale: string;
  role: string;
};

let currentUser: SessionUser | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function getCurrentUser(): SessionUser | null {
  return currentUser;
}

export function setSession(token: string, user: SessionUser) {
  accessToken = token;
  currentUser = user;
  onChange?.();
}

export function clearSession() {
  accessToken = null;
  currentUser = null;
  onChange?.();
}

export function subscribe(fn: () => void): () => void {
  onChange = fn;
  return () => { onChange = null; };
}

// restoreSession silently re-issues an access token from the sm_refresh cookie
// (set HTTP-only by the backend). The in-memory access token is lost on every
// page reload by design, so any page that needs auth on load calls this first.
// Returns the user on success, or null when there is no valid session.
export async function restoreSession(): Promise<SessionUser | null> {
  try {
    const sr = await api<{ accessToken: string; user: SessionUser }>(
      '/api/auth/refresh',
      { method: 'POST' },
    );
    setSession(sr.accessToken, sr.user);
    return sr.user;
  } catch {
    clearSession();
    return null;
  }
}

export async function logout(): Promise<void> {
  try {
    await api('/api/auth/logout', { method: 'POST' });
  } catch {
    // ignore — clear local state regardless
  }
  clearSession();
}
